import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Profile } from '../../types/profile';
import { School, SchoolLevels, SchoolLevelsShortMap } from '../../types/school';
import { SchoolAuthorityDetail, SchoolBasicInfo } from '../../types/rest-api';
import {
  UserRole,
  ActiveContext,
  RoleService,
} from '../../services/role.service';
import { MediaMatcher } from '@angular/cdk/layout';
import { ChatClientService } from 'stream-chat-angular';
import {
  filter,
  from,
  interval,
  map,
  of,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { SchoolService } from '../../services/school.service';
import { FormsModule } from '@angular/forms';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { RoundImageComponent } from '../round-image/round-image.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatButton } from '@angular/material/button';
import { MatNavList, MatListItem, MatDivider } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  OrderProcessComponent,
  type OrderProcessComponentProps,
} from 'src/app/dialogs/order-process/order-process.component';
import { NavbarEntitySwitcherPrimaryComponent } from './navbar-entity-switcher/navbar-entity-switcher-primary.component';
import { AuthorityPermissionService } from '../../services/authority-permission.service';
import { SchoolPermissionService } from '../../services/school-permission.service';
import { StaffService } from '../../services/staff.service';
import { ApplicationUnreadService } from '../../services/application-unread.service';
import { isSchuleinheitPaidPlanSidebarAcknowledged } from '../../core/schuleinheit-pro-sidebar-flag';
const UNREAD_APPLICATIONS_POLL_MS = 60_000;
/**
 * One navigation fires NavigationEnd, the `schools` input and activeContext$ in
 * quick succession; collapse those into a single check. A later navigation
 * always re-checks, so the sidebar stays current.
 */
const SCHULEINHEIT_UPGRADE_RECHECK_MS = 2_000;

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    RouterLink,
    RoundImageComponent,
    MatTooltip,
    MatNavList,
    MatListItem,
    RouterLinkActive,
    MatIcon,
    MatDivider,
    MatProgressSpinner,
    NavbarEntitySwitcherPrimaryComponent,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatButton,
  ],
})
export class NavbarComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
  private chatService = inject(ChatClientService);
  private dialog = inject(MatDialog);
  protected roleService = inject(RoleService);
  private router = inject(Router);
  private schoolService = inject(SchoolService);
  private cdr = inject(ChangeDetectorRef);
  private readonly authorityPermissionService = inject(
    AuthorityPermissionService
  );
  private readonly schoolPermissionService = inject(SchoolPermissionService);
  private readonly staffService = inject(StaffService);
  private readonly applicationUnread = inject(ApplicationUnreadService);

  readonly isStaff = this.staffService.isStaff;
  readonly unreadApplicationCount = this.applicationUnread.count;
  private unreadApplicationsScopeId: string | null = null;

  meineSchuleExpanded = true;
  meineOrgExpanded = true;
  private authorityFirstSchoolId: string | null = null;
  entityDropdownOpen = false;

  @Input() profile?: Profile;
  @Input() userRole: UserRole = 'fachkraft';
  @Input() schools: School[] = [];
  @Input() authorities: SchoolAuthorityDetail[] = [];
  @Input() logoutInProgress: boolean = false;
  /** Off when the mobile toolbar already shows the logo. */
  @Input() showLogo: boolean = true;

  @Output() onLogout = new EventEmitter<void>();
  @Output() onNavigate = new EventEmitter<void>();
  @Output() onContextSwitch = new EventEmitter<ActiveContext>();

  /** A page is still resolving the context ({@link RoleService.awaitPageContext}). */
  get contextResolving(): boolean {
    return this.roleService.contextResolvingSignal();
  }

  get selectedEntityValue(): string {
    const ctx = this.roleService.activeContext;
    if (ctx) return `${ctx.type}:${ctx.id}`;
    if (this.authorities.length > 0)
      return `authority:${this.authorities[0].id}`;
    if (this.schools.length > 0) return `school:${this.schools[0].id}`;
    return '';
  }

  minQuery: MediaQueryList;
  maxQuery: MediaQueryList;
  private readonly _mobileQueryListener: () => void;
  unreadMessageCount: number = 0;

  /** After GET /schools/{id}/detail (see `SchoolDetailExtended.authorized_for_modules`). */
  schuleinheitSidebarUpgradeVisible = false;
  private schuleinheitUpgradeVisibleForSchoolId: string | null = null;
  private schuleinheitUpgradeFetchSub?: Subscription;
  private schuleinheitUpgradeRequestedFor: string | null = null;
  private schuleinheitUpgradeRequestedAt = 0;
  private readonly navHintSub = new Subscription();

  constructor() {
    const media = inject(MediaMatcher);

    this.minQuery = media.matchMedia('(max-width: 1023px)');
    this.maxQuery = media.matchMedia('(min-width: 768px)');
    this._mobileQueryListener = () => {
      this.cdr.detectChanges();
    };
    this.minQuery.addListener(this._mobileQueryListener);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userRole'] || changes['schools']) {
      this.refreshSchuleinheitUpgradeSidebar();
    }
    // The layout binds `authorities`/`userRole` asynchronously, after this
    // component's ngOnInit. Load the authority role once they arrive, otherwise
    // the role stays `null` and the Inserate/Struktur/Profil links never show.
    if (changes['userRole'] || changes['authorities']) {
      this.ensureAuthorityRoleLoaded();
    }
    this.ensureSchoolCapabilitiesLoaded();
  }

  /** Fetch the caller's authority role once `activeAuthorityId` is known. */
  private ensureAuthorityRoleLoaded(): void {
    const id = this.activeAuthorityId;
    if (id) {
      this.authorityPermissionService.loadRole(id);
    }
  }

  /**
   * Fetch the caller's per-school capabilities once `activeSchoolId` resolves.
   * `schools`, {@link RoleService.activeContext} and `authorityFirstSchoolId` all
   * arrive asynchronously, after this component's ngOnInit — so the
   * `updateRouteState()` call site alone only fires the request if a navigation
   * happens to land after that data. Without this the capability signal stays
   * `null`, the `canView*` flags report `false`, and «Inserate & Bewerbungen» /
   * «Strukturverwaltung» / «Profil & Auftritt» never show until the user
   * navigates again. `loadCapabilities` is fetch-once, so extra calls are free.
   */
  private ensureSchoolCapabilitiesLoaded(): void {
    const id = this.activeSchoolId;
    if (id) {
      this.schoolPermissionService.loadCapabilities(id);
    }
  }

  /**
   * Scope ids, role and capabilities all resolve asynchronously (see
   * {@link ensureSchoolCapabilitiesLoaded}); re-count as soon as the scope the
   * «Inserate & Bewerbungen» link points at settles or changes.
   */
  ngDoCheck(): void {
    const scopeId = this.applicationsBadgeScopeId;
    if (scopeId !== this.unreadApplicationsScopeId) {
      // TODO(unread-badge): temporary debug logging, remove once the badge works.
      console.log('[unread-badge] scope change', {
        from: this.unreadApplicationsScopeId,
        to: scopeId,
        userRole: this.userRole,
        ctx: this.roleService.activeContext,
        showsMeineSchulen: this.schulgemeindeShowsMeineSchulenNav,
        activeAuthorityId: this.activeAuthorityId,
        showJobsLink: this.activeAuthorityId ? this.showJobsLink : 'n/a',
        activeSchoolId: this.activeSchoolId,
        schoolCapsSettled: this.schoolCapabilitiesSettled,
        hrLink: this.schulgemeindeHrJobsLink,
      });
      this.unreadApplicationsScopeId = scopeId;
      this.applicationUnread.refresh(scopeId);
    }
  }

  /**
   * Id the visible «Inserate & Bewerbungen» link opens (authority or Schuleinheit
   * root), or `null` when that link is hidden — no badge, and no request that
   * would 403 for users without HR access.
   */
  get applicationsBadgeScopeId(): string | null {
    if (this.userRole === 'schulgemeinde') {
      if (this.schulgemeindeShowsMeineSchulenNav) {
        return this.schoolCapabilitiesSettled && this.showSchoolJobsLink
          ? this.selectedSchoolUnitId
          : null;
      }
      if (!this.activeAuthorityId || !this.showJobsLink) return null;
      const link = this.schulgemeindeHrJobsLink;
      return link.length === 3 ? link[1] : null;
    }
    if (this.userRole === 'schuleinheit') {
      return this.schoolCapabilitiesSettled && this.showSchoolJobsLink
        ? this.activeSchoolId
        : null;
    }
    return null;
  }

  ngOnDestroy(): void {
    this.minQuery.removeListener(this._mobileQueryListener);
    this.navHintSub.unsubscribe();
    this.schuleinheitUpgradeFetchSub?.unsubscribe();
  }

  ngOnInit(): void {
    this.staffService.ensureLoaded().subscribe();

    this.chatService.user$
      .pipe(
        switchMap(user =>
          from(this.chatService.chatClient.getUnreadCount(user?.id))
        )
      )
      .subscribe(({ total_unread_count }) => {
        this.unreadMessageCount = total_unread_count;
      });

    this.roleService.activeContext$
      .pipe(
        switchMap(ctx =>
          ctx?.type === 'authority'
            ? this.schoolService.getByAuthority(ctx.id).pipe(
                map(schools => {
                  const root = schools.find(
                    s => s.id && s.rootId && s.id === s.rootId
                  );
                  return (
                    root?.id ?? schools[0]?.rootId ?? schools[0]?.id ?? null
                  );
                })
              )
            : of(null)
        )
      )
      .subscribe(id => {
        this.authorityFirstSchoolId = id;
        this.ensureSchoolCapabilitiesLoaded();
      });

    const updateRouteState = (url: string) => {
      const cleanUrl = url.split('?')[0];
      this.currentPath = cleanUrl;

      this.isJobsRoute =
        (url.startsWith('/my-schools') &&
          !url.includes('/structure') &&
          !url.includes('/schools/')) ||
        (url.startsWith('/my-organization/') &&
          url.includes('/jobs') &&
          !url.includes('/schools/'));

      this.isMySchoolProfileRoute =
        (url.startsWith('/my-schools/') && url.includes('/schools/')) ||
        (url.startsWith('/my-organization/') && url.includes('/schools/'));

      // Träger-Profil: only `/my-organization/:id`, not `/jobs`, `/structure`, etc.
      this.isOrgProfileRoute = /^\/my-organization\/[^/]+$/.test(cleanUrl);

      // Documents lives under /applications/my, so without this both
      // "Meine Bewerbungen" and "Bewerbungsdossier" would be highlighted.
      this.isApplicationDocumentsRoute = cleanUrl.startsWith(
        '/applications/my/documents'
      );

      this.meineOrgExpanded =
        this.meineOrgExpanded ||
        url.startsWith('/my-organization') ||
        (this.userRole === 'schulgemeinde' && this.isJobsRoute);

      this.meineSchuleExpanded =
        this.meineSchuleExpanded || url.startsWith('/my-schools/');

      const id = this.activeAuthorityId;
      if (id) {
        this.authorityPermissionService.loadRole(id);
      }
      this.ensureSchoolCapabilitiesLoaded();
    };

    updateRouteState(this.router.url);

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        updateRouteState(e.urlAfterRedirects);
        this.refreshSchuleinheitUpgradeSidebar();
        this.applicationUnread.refreshIfStale(this.unreadApplicationsScopeId);
      });

    // New applications arrive without any client-side event (unlike Stream Chat),
    // so poll while the tab is visible.
    this.navHintSub.add(
      interval(UNREAD_APPLICATIONS_POLL_MS)
        .pipe(filter(() => document.visibilityState === 'visible'))
        .subscribe(() =>
          this.applicationUnread.refresh(this.unreadApplicationsScopeId)
        )
    );

    this.navHintSub.add(
      this.roleService.activeContext$.subscribe(() => {
        this.refreshSchuleinheitUpgradeSidebar();
        this.ensureAuthorityRoleLoaded();
        this.ensureSchoolCapabilitiesLoaded();
      })
    );
    this.refreshSchuleinheitUpgradeSidebar();
  }

  logout() {
    this.onLogout.emit();
    this.onNavigate.emit();
  }

  navigate() {
    this.onNavigate.emit();
  }

  toggleMeineSchule() {
    this.meineSchuleExpanded = !this.meineSchuleExpanded;
  }

  toggleMeineOrg() {
    this.meineOrgExpanded = !this.meineOrgExpanded;
  }

  onEntityChange(value: string): void {
    const [type, id] = value.split(':');
    if (type === 'authority') {
      const authority = this.authorities.find(a => a.id === id);
      if (authority) {
        this.onContextSwitch.emit({
          type: 'authority',
          id,
          name: authority.name,
        });
      }
    } else if (type === 'school') {
      const school = this.schools.find(s => s.id === id);
      if (school) {
        this.onContextSwitch.emit({
          type: 'school',
          id,
          name: school.name ?? '',
        });
      }
    }
  }

  get activeSchoolId(): string | null {
    if (this.selectedEntityValue.startsWith('school:')) {
      return this.selectedEntityValue.split(':')[1];
    }
    const ctx = this.roleService.activeContext;
    if (ctx?.type === 'authority') {
      return this.authorityFirstSchoolId;
    }
    return this.schools[0]?.id || null;
  }

  /**
   * Schulgemeinde user picked a Schuleinheit in the top switcher → sidebar shows
   * «Meine Schulen» with `/my-schools/...` links (not `/my-organization/...`, which
   * would overwrite {@link RoleService.activeContext} via URL sync).
   */
  get schulgemeindeShowsMeineSchulenNav(): boolean {
    return (
      this.userRole === 'schulgemeinde' &&
      this.roleService.activeContext?.type === 'school'
    );
  }

  /** Root school id when the switcher context is a Schuleinheit; drives Träger «Meine Schulen» links. */
  get selectedSchoolUnitId(): string | null {
    const ctx = this.roleService.activeContext;
    return ctx?.type === 'school' ? ctx.id : null;
  }

  /**
   * Schulgemeinde HR entry (Inserate verwalten): same target as «Inserate & Bewerbungen».
   * Sidebar «Stellen» uses this so Träger admins reach `/my-schools/.../jobs`, not public `/jobs`.
   *
   * Prefer the top entity switcher (`selectedEntityValue`) so the HR portal matches what the
   * user selected (Schulgemeinde vs Schuleinheit) even before any `/my-organization/...` URL
   * has been visited — `routerLink` alone could fail to navigate in that state.
   */
  get schulgemeindeHrJobsLink(): string[] {
    const sel = this.selectedEntityValue;
    if (sel.startsWith('authority:')) {
      const id = sel.slice('authority:'.length);
      if (id) {
        return ['/my-organization', id, 'jobs'];
      }
    } else if (sel.startsWith('school:')) {
      const id = sel.slice('school:'.length);
      if (id) {
        return ['/my-schools', id, 'jobs'];
      }
    }
    const ctx = this.roleService.activeContext;
    if (ctx?.type === 'authority') {
      return ['/my-organization', ctx.id, 'jobs'];
    }
    if (ctx?.type === 'school' && this.activeSchoolId) {
      return ['/my-schools', this.activeSchoolId, 'jobs'];
    }
    if (this.activeAuthorityId) {
      return ['/my-organization', this.activeAuthorityId, 'jobs'];
    }
    if (this.activeSchoolId) {
      return ['/my-schools', this.activeSchoolId, 'jobs'];
    }
    return ['/my-schools'];
  }

  /** Programmatic navigation so HR always opens (see `schulgemeindeHrJobsLink` doc). */
  openSchulgemeindeHrJobs(event: MouseEvent): void {
    event.preventDefault();
    void this.router.navigate(this.schulgemeindeHrJobsLink);
    this.navigate();
  }

  get activeAuthorityId(): string | null {
    const ctx = this.roleService.activeContext;
    if (ctx?.type === 'authority') {
      return ctx.id;
    }
    // Schulgemeinde user may switch the top dropdown to a Schuleinheit; org
    // routes still need a stable authority id for Struktur / Träger-Profil / HR.
    if (this.userRole === 'schulgemeinde' && this.authorities.length > 0) {
      return this.authorities[0].id;
    }
    return null;
  }

  isMySchoolProfileRoute = false;
  isOrgProfileRoute = false;
  isApplicationDocumentsRoute = false;
  isJobsRoute = false;
  private currentPath = '';

  /** Settings or the user's own profile: the account row shows as the active entry. */
  get isAccountRoute(): boolean {
    if (this.currentPath.startsWith('/settings')) return true;
    const ownId = this.profile?.id;
    if (!ownId) return false;
    const ownProfile = `/me/${ownId}`;
    return (
      this.currentPath === ownProfile ||
      this.currentPath.startsWith(`${ownProfile}/`)
    );
  }

  get showProfileLink(): boolean {
    const id = this.activeAuthorityId;
    return id ? this.authorityPermissionService.canViewProfile(id)() : true;
  }

  get showStructureLink(): boolean {
    const id = this.activeAuthorityId;
    return id ? this.authorityPermissionService.canViewStructure(id)() : true;
  }

  get showJobsLink(): boolean {
    const id = this.activeAuthorityId;
    return id ? this.authorityPermissionService.canViewJobs(id)() : true;
  }

  /**
   * `true` once GET /v1/permissions/schools/{id}/my-role for the active school
   * resolved or failed. The `canView*` flags cannot express "still loading"
   * (`null` capabilities read as `false`), so the template hides the whole group
   * until this flips rather than rendering rows the user may not have.
   */
  get schoolCapabilitiesSettled(): boolean {
    const id = this.activeSchoolId;
    return id ? this.schoolPermissionService.settled(id)() : false;
  }

  get showSchoolJobsLink(): boolean {
    const id = this.activeSchoolId;
    return id ? this.schoolPermissionService.canViewJobs(id)() : true;
  }

  get showSchoolStructureLink(): boolean {
    const id = this.activeSchoolId;
    return id ? this.schoolPermissionService.canViewStructure(id)() : true;
  }

  get showSchoolProfileLink(): boolean {
    const id = this.activeSchoolId;
    return id ? this.schoolPermissionService.canViewProfile(id)() : true;
  }

  /** Fachkraft → fullscreen order flow. `$event`: pass from template so backdrop/focus resets after dialog. */
  openUpgradeDialog(event: MouseEvent) {
    const opener = event.currentTarget as HTMLElement | null;
    const ref = this.dialog.open<OrderProcessComponent>(OrderProcessComponent, {
      minWidth: '100vw',
      minHeight: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      disableClose: true,
      autoFocus: false,
    });
    this.afterFullscreenOrderDialog(ref, opener);
  }

  /**
   * Schuleinheit: plan step only. Do not call `navigate()` here — toggling sidenav with the dialog
   * overlay stacks/backdrops incorrectly and keeps hover/focus on this list row.
   */
  openSchuleinheitPaidUpgradeDialog(event: MouseEvent) {
    const opener = event.currentTarget as HTMLElement | null;
    const schoolId = this.schuleinheitUpgradeSchoolIdForSidebar();
    if (!schoolId) return;
    const ref = this.dialog.open<OrderProcessComponent>(OrderProcessComponent, {
      minWidth: '100vw',
      minHeight: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      disableClose: true,
      autoFocus: false,
      data: {
        schuleinheitExistingSchoolId: schoolId,
      } satisfies OrderProcessComponentProps,
    });
    this.afterFullscreenOrderDialog(ref, opener);
  }

  private afterFullscreenOrderDialog(
    ref: MatDialogRef<OrderProcessComponent>,
    opener: HTMLElement | null
  ): void {
    ref.afterClosed().subscribe(() => {
      requestAnimationFrame(() => {
        opener?.blur();
        const ae = document.activeElement as HTMLElement | null;
        if (ae && opener?.contains?.(ae)) {
          ae.blur?.();
        }
        this.cdr.markForCheck();
      });
    });
  }

  /**
   * Schuleinheit sidebar upgrade: show unless school detail reports full HR
   * (`authorized_for_modules.jobs === true`).
   */
  showSchuleinheitPaidUpgradeInSidebar(): boolean {
    return this.schuleinheitSidebarUpgradeVisible;
  }

  private refreshSchuleinheitUpgradeSidebar(): void {
    const candidateId = this.schuleinheitUpgradeSchoolIdForSidebar();
    if (
      candidateId !== null &&
      candidateId === this.schuleinheitUpgradeRequestedFor &&
      Date.now() - this.schuleinheitUpgradeRequestedAt <
        SCHULEINHEIT_UPGRADE_RECHECK_MS &&
      this.roleService.isOwnerOfSchool(candidateId) &&
      !isSchuleinheitPaidPlanSidebarAcknowledged(candidateId)
    ) {
      return;
    }

    this.schuleinheitUpgradeFetchSub?.unsubscribe();
    this.schuleinheitUpgradeRequestedFor = null;

    if (this.userRole !== 'schuleinheit' || !this.schools?.length) {
      this.setSchuleinheitUpgradeVisible(null, false);
      return;
    }

    const schoolId = this.schuleinheitUpgradeSchoolIdForSidebar();
    if (!schoolId) {
      this.setSchuleinheitUpgradeVisible(null, false);
      return;
    }

    // Upgrade is an Owner-CTA: invited SCHOOL_ADMINs of a Schuleinheit under a paid
    // Schulgemeinde must not see it, even if our own free-plan school sits in the same
    // account. Gate on OWNER access of the active root school.
    if (!this.roleService.isOwnerOfSchool(schoolId)) {
      this.setSchuleinheitUpgradeVisible(null, false);
      return;
    }

    // Keep the last confirmed value for the same school while revalidating —
    // resetting to false here made the entry flicker on every navigation.
    if (schoolId !== this.schuleinheitUpgradeVisibleForSchoolId) {
      this.setSchuleinheitUpgradeVisible(null, false);
    }

    // Free tier still has a Schuleinheit subscription line on the account; use
    // GET …/detail `authorized_for_modules.jobs` (same as HR / «Meine Schule» locking).
    this.schuleinheitUpgradeRequestedFor = schoolId;
    this.schuleinheitUpgradeRequestedAt = Date.now();
    this.schuleinheitUpgradeFetchSub = this.schoolService
      .showSchuleinheitPaidUpgradeForSchoolDetail(schoolId)
      .pipe(take(1))
      .subscribe({
        next: visible => {
          this.setSchuleinheitUpgradeVisible(schoolId, visible);
        },
      });
  }

  private setSchuleinheitUpgradeVisible(
    schoolId: string | null,
    visible: boolean
  ): void {
    this.schuleinheitUpgradeVisibleForSchoolId = schoolId;
    this.schuleinheitSidebarUpgradeVisible = visible;
    this.cdr.markForCheck();
  }

  /**
   * Root school id for GET …/detail (module entitlements / `authorized_for_modules` live on
   * the root). Nav `schools` inputs omit `rootId`/`parent_id`, so we derive from
   * {@link RoleService.mySchools} (`SchoolBasicInfo.parent_id`).
   */
  private schuleinheitUpgradeSchoolIdForSidebar(): string | null {
    if (this.userRole !== 'schuleinheit' || this.schools.length === 0) {
      return null;
    }
    const items = this.roleService.mySchools;
    const allBasic = items.map(u => u.school);
    if (allBasic.length === 0) {
      return this.schools[0]?.id ?? null;
    }
    const ctx = this.roleService.activeContext;
    let picked: SchoolBasicInfo | undefined =
      ctx?.type === 'school'
        ? items.find(u => u.school.id === ctx.id)?.school
        : undefined;
    picked ??= items[0]?.school;
    if (!picked) {
      return this.schools[0]?.id ?? null;
    }
    return this.rootSchoolIdForSidebarDetail(picked, allBasic);
  }

  /** Walk `parent_id` to the root school in the administered list. */
  private rootSchoolIdForSidebarDetail(
    school: SchoolBasicInfo,
    all: SchoolBasicInfo[]
  ): string {
    const byId = new Map(all.map(s => [s.id, s]));
    let cur: SchoolBasicInfo | undefined = school;
    const visited = new Set<string>();
    while (cur?.parent_id) {
      if (visited.has(cur.id)) break;
      visited.add(cur.id);
      const parent = byId.get(cur.parent_id);
      if (!parent) break;
      cur = parent;
    }
    return cur?.id ?? school.id;
  }

  getJobLevels(levels: (typeof SchoolLevels)[number][]) {
    if (!levels) return '';
    if (
      levels.includes('Kindergarten') &&
      levels.includes('Unterstufe') &&
      levels.includes('Mittelstufe')
    ) {
      return 'Kiga - Primar';
    }
    if (levels.includes('Unterstufe') && levels.includes('Mittelstufe')) {
      return 'Primar';
    }
    if (levels.includes('Kindergarten') && levels.includes('Unterstufe')) {
      return 'KUST';
    }

    return levels
      .map(level => SchoolLevelsShortMap[level] || level)
      .join(' - ');
  }

  getJobTitle() {
    const firstJobFunction = this.profile?.jobFunctions?.[0];
    if (!firstJobFunction?.name) return '';
    const inStudy = firstJobFunction.inStudy ? ' i.A.' : '';
    return `${firstJobFunction.name}${inStudy}`;
  }

  showTooltips() {
    return true;
  }

  getAvatarUrl(profile?: Profile) {
    if (profile?.avatar?.path) {
      return profile.avatar.path;
    }

    return 'assets/defaults/Profil.png';
  }
}
